<template>
	<Header :title="props.title" />

	<div class="details-tabs">
		<Tabs
			:items="tabItems"
			:model-value="activeTab"
			@update:model-value="handleTabClick" />
	</div>
	<HeaderLoader :is-loading="loaders.size > 0" />

	<div class="details-panels">
		<Container class="details">
			<Form
				v-if="activeTab === 'account'"
				:id="resolvedUserId"
				is-edit
				@start-loading="loaders.add('form')"
				@stop-loading="loaders.delete('form')" />
			<Usage
				v-else-if="activeTab === 'usage'"
				:route-name="props.routeName"
				:route-params="props.routeParams"
				:user-id="props.userId"
				@update:search="handleSearchUpdate" />
			<PurchaseHistory
				v-else-if="activeTab === 'purchases'"
				class="details__purchase-history"
				:route-name="props.routeName"
				:route-params="props.routeParams"
				:user-id="props.userId"
				@update:search="handleSearchUpdate" />
			<ApiTokens
				v-else-if="activeTab === 'api-tokens'"
				@start-loading="loaders.add('api-tokens')"
				@stop-loading="loaders.delete('api-tokens')" />
			<Apps v-else-if="activeTab === 'apps'" />
			<Mcp v-else-if="activeTab === 'mcp'" />
			<NotificationSettings
				v-else-if="activeTab === 'notifications'"
				:key="resolvedUserId"
				:user-id="resolvedUserId" />
			<FeatureFlags
				v-else-if="activeTab === 'feature-flags'"
				:user-id="Number(resolvedUserId)" />
		</Container>
	</div>
</template>

<script setup lang="ts">
import { reactive, computed } from 'vue'
import Header from '@/components/Header.vue'
import Form from './components/Form.vue'
import HeaderLoader from '@/components/HeaderLoader.vue'
import Container from '@/components/Container.vue'
import { useAuthStore } from '@/stores/Auth'
import PurchaseHistory from './components/PurchaseHistory.vue'
import FeatureFlags from './components/FeatureFlags.vue'
import Tabs from '@/components/Tabs.vue'
import { useRoute, useRouter } from 'vue-router'
import Usage from './components/Usage.vue'
import ApiTokens from './components/ApiTokens.vue'
import Apps from './components/Apps.vue'
import Mcp from './components/Mcp.vue'
import NotificationSettings from './components/NotificationSettings.vue'

const props = withDefaults(
	defineProps<{
		userId?: number
		routeName?: string
		routeParams?: Record<string, string | number>
		title?: string
	}>(),
	{
		userId: undefined,
		routeName: 'account-details',
		routeParams: () => ({}),
		title: 'Account Details',
	},
)

const authStore = useAuthStore()
const resolvedUserId = computed(() => props.userId ?? authStore.user!.id)
const isAdminView = computed(() => props.userId !== undefined)
const loaders = reactive(new Set<string>())

const route = useRoute()
const router = useRouter()

const { tabItems, activeTab, handleTabClick } = useTabs()

function handleSearchUpdate(searchValue: string) {
	const query: Record<string, string> = { tab: activeTab.value }
	if (searchValue) query.s = searchValue
	router.replace({ name: props.routeName, params: props.routeParams, query })
}

function useTabs() {
	const showMonthlyAllowanceNotifications = computed(() =>
		authStore.hasFeatureFlag('monthly-allowance'),
	)

	const showDeveloperTabs = computed(
		() => authStore.hasFeatureFlag('beta-features') && !isAdminView.value,
	)

	const validTabs = computed(() => {
		const tabs = ['account', 'usage', 'purchases']
		if (showMonthlyAllowanceNotifications.value) tabs.push('notifications')
		if (showDeveloperTabs.value) tabs.push('api-tokens', 'apps', 'mcp')
		if (isAdminView.value) tabs.push('feature-flags')
		return tabs
	})

	const activeTab = computed(() =>
		validTabs.value.includes(String(route.query.tab)) ? (route.query.tab as string) : 'account',
	)

	const tabItems = computed(() => {
		const items = [{ label: 'Account', value: 'account' }]

		if (showMonthlyAllowanceNotifications.value) {
			items.push({ label: 'Notifications', value: 'notifications' })
		}

		items.push({ label: 'Purchases', value: 'purchases' })
		items.push({ label: 'Usage', value: 'usage' })

		if (showDeveloperTabs.value) {
			items.push({ label: 'API Tokens', value: 'api-tokens' })
			items.push({ label: 'Apps', value: 'apps' })
			items.push({ label: 'MCP', value: 'mcp' })
		}
		if (isAdminView.value) {
			items.push({ label: 'Feature Flags', value: 'feature-flags' })
		}
		return items
	})

	function handleTabClick(tab: string) {
		const query: Record<string, string> = { tab }
		router.replace({ name: props.routeName, params: props.routeParams, query })
	}

	return {
		tabItems,
		activeTab,
		handleTabClick,
	}
}
</script>

<style lang="scss" scoped>
.details-tabs {
	width: 100%;
}

.details-panels {
	width: 100%;
	overflow: auto;
	display: flex;
	justify-content: center;

	& > * {
		max-width: 1200px;
		width: 100%;
	}

	.details {
		display: grid;
		grid-template-columns: 1fr;
		gap: 11px;
		justify-items: center;
		align-items: start;
	}
}
</style>
